import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Users, Trash2, Mail, Phone, Loader2, Shield, Sparkles } from "lucide-react";

const roleMeta = {
  owner: { label: "Owner", className: "bg-primary text-primary-foreground", icon: Shield },
  admin: { label: "Admin", className: "bg-violet-100 text-violet-700", icon: Shield },
  cleaner: { label: "Cleaner", className: "bg-secondary text-secondary-foreground", icon: Sparkles },
};

const blankForm = { name: "", email: "", password: "", phone: "", role: "cleaner" };

export default function Cleaners() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blankForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = () => api.get("/team").then((r) => setMembers(r.data));
  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    if (!form.name || !form.email || !form.password)
      return toast.error("Name, email and password are required");
    setSaving(true);
    try {
      await api.post("/team", form);
      toast.success(`${form.role === "admin" ? "Admin" : "Cleaner"} account created`);
      setOpen(false);
      setForm(blankForm);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create account");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/team/${id}`);
      toast.success("Member removed");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove member");
    }
  };

  const changeRole = async (member, role) => {
    if (role === member.role) return;
    try {
      await api.put(`/team/${member.id}/role`, { role });
      toast.success(`${member.name} is now ${role === "admin" ? "an Admin" : "a Cleaner"}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not change role");
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="text-xs tracking-[0.15em] uppercase font-bold text-primary mb-1">People</p>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Team</h1>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="add-cleaner-button">
          <Plus className="h-4 w-4 mr-2" /> Add member
        </Button>
      </div>

      <p className="text-sm text-muted-foreground mb-6">
        <b className="text-foreground">Admins</b> see everything and can assign cleaners (full access,
        like you). <b className="text-foreground">Cleaners</b> only see their assigned jobs and pay.
      </p>

      {members.length === 0 ? (
        <Card className="p-12 text-center">
          <Users className="h-10 w-10 text-muted-foreground mx-auto mb-4" strokeWidth={1.3} />
          <p className="text-muted-foreground">No team members yet.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {members.map((c) => {
            const meta = roleMeta[c.role] || roleMeta.cleaner;
            const isSelf = c.id === user?.id;
            const canDelete = c.role !== "owner" && !isSelf;
            return (
              <Card key={c.id} className="p-5 flex items-center gap-4" data-testid={`cleaner-${c.id}`}>
                <div
                  className="h-12 w-12 rounded-full text-primary-foreground flex items-center justify-center font-semibold text-lg shrink-0"
                  style={{ backgroundColor: c.role === "cleaner" ? "#4A7C59" : "#7C3AED" }}
                >
                  {c.name?.[0]?.toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-medium truncate">{c.name}</p>
                    <Badge className={`${meta.className} border-0 text-[10px]`}>
                      {meta.label}
                      {isSelf ? " · You" : ""}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground flex items-center gap-1 truncate mt-0.5">
                    <Mail className="h-3 w-3" /> {c.email}
                  </p>
                  {c.phone && (
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Phone className="h-3 w-3" /> {c.phone}
                    </p>
                  )}
                </div>
                {canDelete && (
                  <div className="flex items-center gap-2 shrink-0">
                    <Select value={c.role} onValueChange={(v) => changeRole(c, v)}>
                      <SelectTrigger
                        className="h-9 w-[112px] text-xs"
                        data-testid={`role-select-${c.id}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cleaner">Cleaner</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDeleteTarget(c)}
                      data-testid={`delete-cleaner-${c.id}`}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display">Add team member</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="member-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cleaner">Cleaner — sees only their jobs &amp; pay</SelectItem>
                  <SelectItem value="admin">Admin — full access, can assign cleaners</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Full name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="cleaner-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="cleaner-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Temporary password</Label>
              <Input
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="cleaner-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Phone (optional)</Label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={save} disabled={saving} data-testid="save-cleaner-button">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this member?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.name}'s account will be removed and they'll lose access. This cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => remove(deleteTarget.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-cleaner"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
